import type { ColumnDef } from '@tanstack/react-table'
import { Printer } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { Fact, Facts } from 'shared/components/facts'
import { PageFrame } from 'shared/components/page-frame'
import { ScanInput } from 'shared/components/scan-input'
import { SectionTitle } from 'shared/components/section'
import { ToneBanner } from 'shared/components/tone-banner'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'shared/components/ui/dialog'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { cn } from 'shared/lib/utils'
import { DamageDialog } from 'shared/components/damage-act'
import { ReturnLabelPrint, type ReturnLabelData } from './ReturnLabel'

/**
 * «Возвраты» — the Point's side of the return flow (ADR-0037, spec §18; ticket 42).
 *
 * - **Accepting a buyer's return.** The operator scans or types what the buyer shows —
 *   the return's code, the client code, the return code or the document — and finds
 *   the return the partner registered for THIS Point. Registered for another Point,
 *   or not registered at all, the answer says so in words and nothing is accepted.
 *   Found, it opens in a dialog with the buyer and the order; «Принять возврат» puts
 *   it on the shelf for the driver and prints its label in the same dialog. No Shift
 *   is needed; the operator cannot undo the acceptance. The return just accepted may
 *   be recorded damaged from its answer.
 * - **What waits for the driver.** The pickup list the system makes itself: returns,
 *   the partner's «вернуть», re-addressed and other Points' Parcels — each with why.
 *   A return's label prints again from its row.
 *
 * The hub's look: the scan field on top and in focus, the answer under it
 * (`ToneBanner`), the table below; after every act the cursor is back in the field.
 * Every call is injected so the behaviour is tested without a backend.
 */
export type FindRequest = RouterInputs['buyerReturns']['Find']
export type FindAnswer = RouterOutputs['buyerReturns']['Find']
export type FoundReturn = Extract<FindAnswer, { outcome: 'found' }>['returns'][number]
export type AcceptRequest = RouterInputs['buyerReturns']['Accept']
export type AcceptAnswer = RouterOutputs['buyerReturns']['Accept']
export type PickupRow = RouterOutputs['returnFlow']['PickupAtPoint'][number]
type AcceptedLabel = Extract<AcceptAnswer, { outcome: 'accepted' }>['label']

const findViaTrpc = (input: FindRequest) => trpcClient.buyerReturns.Find.mutate(input)
const acceptViaTrpc = (input: AcceptRequest) => trpcClient.buyerReturns.Accept.mutate(input)
const pickupViaTrpc = () => trpcClient.returnFlow.PickupAtPoint.query({})
const labelViaTrpc = (parcelId: string) => trpcClient.buyerReturns.Label.query({ parcelId })

const failure = (error: unknown) => (error instanceof Error ? error.message : String(error))

export interface ReturnsProps {
	find?: (input: FindRequest) => Promise<FindAnswer>
	accept?: (input: AcceptRequest) => Promise<AcceptAnswer>
	pickup?: () => Promise<PickupRow[]>
	label?: (parcelId: string) => Promise<ReturnLabelData>
	print?: () => void
}

/** The answer to the last find or acceptance, as the banner says it. */
type Answer =
	| { outcome: 'refused'; title: string; code: string }
	| { outcome: 'accepted'; label: AcceptedLabel }

/** What the dialog shows: the return(s) found, to accept; or a label, to print. */
type Open =
	| { step: 'accept'; found: FoundReturn[]; chosen: FoundReturn | null }
	| { step: 'label'; label: ReturnLabelData; code: string }

export function Returns({
	find = findViaTrpc,
	accept = acceptViaTrpc,
	pickup = pickupViaTrpc,
	label = labelViaTrpc,
	print,
}: ReturnsProps) {
	const { t } = useTranslate()
	const [answer, setAnswer] = useState<Answer | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [busy, setBusy] = useState(false)
	const [open, setOpen] = useState<Open | null>(null)
	const [waiting, setWaiting] = useState<PickupRow[] | null>(null)
	const [pickupError, setPickupError] = useState<string | null>(null)
	// Bumped after every act, so the scan field remounts and takes the cursor back.
	const [scanRound, setScanRound] = useState(0)
	const refocus = () => setScanRound((n) => n + 1)

	const refreshPickup = useCallback(() => {
		pickup().then(
			(rows) => {
				setWaiting(rows)
				setPickupError(null)
			},
			(e) => setPickupError(failure(e)),
		)
	}, [pickup])
	useEffect(() => refreshPickup(), [refreshPickup])

	const onFind = async (typed: string) => {
		if (busy) return
		setBusy(true)
		setError(null)
		setAnswer(null)
		try {
			const res = await find({ value: typed })
			if (res.outcome === 'found') {
				setOpen({ step: 'accept', found: res.returns, chosen: res.returns.length === 1 ? res.returns[0]! : null })
			} else {
				setAnswer({
					outcome: 'refused',
					code: typed,
					title:
						res.reason === 'return_registered_elsewhere'
							? t('returns.registeredElsewhere', { point: res.pointCode })
							: t(`returnRefusal.${res.reason}`),
				})
			}
		} catch (e) {
			setError(failure(e))
		} finally {
			setBusy(false)
			refocus()
		}
	}

	const onAccept = async (chosen: FoundReturn) => {
		if (busy) return
		setBusy(true)
		setError(null)
		try {
			const res = await accept({ parcelId: chosen.parcelId })
			if (res.outcome === 'refused') {
				setOpen(null)
				setAnswer({ outcome: 'refused', code: chosen.trackingCode, title: t(`returnRefusal.${res.reason}`) })
			} else {
				setAnswer({ outcome: 'accepted', label: res.label })
				setOpen({ step: 'label', label: res.label, code: res.label.trackingCode })
				refreshPickup()
			}
		} catch (e) {
			setOpen(null)
			setError(failure(e))
		} finally {
			setBusy(false)
		}
	}

	const reprint = useCallback(
		async (parcelId: string) => {
			setError(null)
			try {
				const found = await label(parcelId)
				setOpen({ step: 'label', label: found, code: found.trackingCode })
			} catch (e) {
				setError(failure(e))
				setScanRound((n) => n + 1)
			}
		},
		[label],
	)

	const close = () => {
		if (busy) return
		setOpen(null)
	}

	return (
		<div data-testid='returns'>
			<PageFrame title='nav.returns'>
				<div className='flex flex-col gap-8'>
					<section className='flex flex-col gap-3'>
						<ScanInput key={scanRound} onScan={onFind} disabled={busy || open !== null} />
						{error && (
							<p role='alert' className='text-sm text-destructive' data-testid='returns-error'>
								{t('returns.failed', { message: error })}
							</p>
						)}
						{answer ? (
							<ReturnsAnswer answer={answer} onDamageClosed={refocus} />
						) : (
							<p className='text-xs text-muted-foreground'>{t('returns.findHint')}</p>
						)}
					</section>

					<PickupTable rows={waiting} error={pickupError} onLabel={reprint} />
				</div>

				<Dialog open={open !== null} onOpenChange={(next) => !next && close()}>
					<DialogContent
						className={open?.step === 'accept' ? 'sm:max-w-2xl' : 'sm:max-w-md'}
						aria-describedby={undefined}
						data-testid={open?.step === 'label' ? 'returns-label-dialog' : 'returns-accept-dialog'}
						onCloseAutoFocus={(event) => {
							// The cursor goes back to the scan field, not to the button that opened the dialog.
							event.preventDefault()
							refocus()
						}}
					>
						{open?.step === 'accept' && (
							<AcceptStep
								open={open}
								busy={busy}
								onChoose={(chosen) => setOpen({ ...open, chosen })}
								onAccept={onAccept}
								onCancel={close}
							/>
						)}
						{open?.step === 'label' && (
							<>
								<DialogHeader>
									<DialogTitle>{t('returns.labelTitle', { code: open.code })}</DialogTitle>
								</DialogHeader>
								<ReturnLabelPrint key={open.code} label={open.label} printNow print={print} />
								<DialogFooter>
									<Button type='button' variant='outline' data-testid='returns-label-close' autoFocus onClick={close}>
										{t('returns.labelClose')}
									</Button>
								</DialogFooter>
							</>
						)}
					</DialogContent>
				</Dialog>
			</PageFrame>
		</div>
	)
}

/** The return found, its buyer and its order — and, when several were found, which one. */
function AcceptStep({
	open,
	busy,
	onChoose,
	onAccept,
	onCancel,
}: {
	open: Extract<Open, { step: 'accept' }>
	busy: boolean
	onChoose: (chosen: FoundReturn) => void
	onAccept: (chosen: FoundReturn) => void
	onCancel: () => void
}) {
	const { t } = useTranslate()
	const { found, chosen } = open
	return (
		<>
			<DialogHeader>
				<DialogTitle className='text-lg'>
					{chosen || found.length === 1 ? t('returns.dialogTitle') : t('returns.chooseTitle', { count: String(found.length) })}
				</DialogTitle>
			</DialogHeader>

			{found.length > 1 && (
				<div className='flex flex-col gap-1' data-testid='returns-found'>
					{found.map((r) => (
						<button
							key={r.parcelId}
							type='button'
							className={cn(
								'flex items-center gap-3 rounded-md border px-3 py-2 text-left text-sm hover:bg-muted',
								chosen?.parcelId === r.parcelId && 'border-primary bg-muted',
							)}
							data-chosen={chosen?.parcelId === r.parcelId}
							data-testid={`returns-found-${r.parcelId}`}
							onClick={() => onChoose(r)}
						>
							<span className='font-mono'>{r.trackingCode}</span>
							<span>{r.recipientName}</span>
							<span className='ml-auto text-muted-foreground'>{r.partnerName}</span>
						</button>
					))}
				</div>
			)}

			{chosen && (
				<div data-testid='returns-chosen'>
					<Facts>
						<Fact label={t('returns.factCode')}>
							<span className='font-mono font-semibold'>{chosen.trackingCode}</span>
						</Fact>
						<Fact label={t('returns.factBuyer')}>{chosen.recipientName}</Fact>
						<Fact label={t('returns.factClient')}>
							<span className='font-mono'>{chosen.clientRef}</span>
						</Fact>
						<Fact label={t('returns.factPartner')}>{chosen.partnerName}</Fact>
						<Fact label={t('returns.factOrder')}>
							<span className='font-mono'>{chosen.partnerOrderRef}</span>
						</Fact>
						{chosen.returnCode && (
							<Fact label={t('returns.factReturnCode')}>
								<span className='font-mono'>{chosen.returnCode}</span>
							</Fact>
						)}
					</Facts>
				</div>
			)}

			<DialogFooter>
				<Button type='button' variant='outline' onClick={onCancel} disabled={busy}>
					{t('returns.cancel')}
				</Button>
				<Button
					type='button'
					data-testid='returns-accept'
					// The operator confirms with Enter: the hands stay on the scanner.
					autoFocus
					disabled={busy || !chosen}
					onClick={() => chosen && onAccept(chosen)}
				>
					{t('returns.accept')}
				</Button>
			</DialogFooter>
		</>
	)
}

function ReturnsAnswer({ answer, onDamageClosed }: { answer: Answer; onDamageClosed: () => void }) {
	const { t } = useTranslate()
	const ids = {
		testId: 'returns-answer',
		titleTestId: 'returns-answer-title',
		detailTestId: 'returns-answer-detail',
		attributes: { 'data-outcome': answer.outcome },
	}
	if (answer.outcome === 'refused') {
		return <ToneBanner tone='refused' {...ids} title={answer.title} detail={answer.code} />
	}
	return (
		<ToneBanner
			tone='ready'
			{...ids}
			title={t('returns.acceptedTitle')}
			detail={t('returns.acceptedDetail', { code: answer.label.trackingCode, partner: answer.label.partnerName })}
			actions={
				<DamageDialog
					key={answer.label.parcelId}
					parcelId={answer.label.parcelId}
					code={answer.label.trackingCode}
					testId='returns-damage'
					onClosed={onDamageClosed}
				/>
			}
		/>
	)
}

/** What waits for the driver, each with why; a return's label prints again from its row. */
function PickupTable({
	rows,
	error,
	onLabel,
}: {
	rows: PickupRow[] | null
	error: string | null
	onLabel: (parcelId: string) => void
}) {
	const { t } = useTranslate()
	const columns = useMemo<ColumnDef<PickupRow>[]>(
		() => [
			{
				id: 'code',
				header: t('returns.colCode'),
				cell: ({ row }) => <span className='font-mono'>{row.original.trackingCode}</span>,
			},
			{ id: 'partner', header: t('returns.colPartner'), cell: ({ row }) => row.original.partnerName },
			{
				id: 'reason',
				header: t('returns.colReason'),
				cell: ({ row }) => (
					<Badge variant={row.original.kind === 'return' ? 'secondary' : 'outline'}>
						{t(`pickupReason.${row.original.reason}`)}
					</Badge>
				),
			},
			{
				id: 'actions',
				header: '',
				meta: { cellClassName: 'w-px text-right' },
				cell: ({ row }) =>
					row.original.kind === 'return' && (
						<Button
							size='sm'
							variant='ghost'
							data-testid={`returns-label-${row.original.parcelId}`}
							onClick={() => onLabel(row.original.parcelId)}
						>
							<Printer />
							{t('returns.labelAgain')}
						</Button>
					),
			},
		],
		[t, onLabel],
	)
	const data = useMemo(() => rows ?? [], [rows])
	const { table } = useDataTable({
		data,
		columns,
		rowCount: data.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		getRowId: (row) => row.parcelId,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})
	return (
		<section className='flex flex-col gap-2' data-testid='returns-pickup'>
			<SectionTitle title={t('returns.pickupTitle')} count={rows?.length} hint={t('returns.pickupHint')} />
			{error ? (
				<p className='text-sm text-destructive' data-testid='returns-pickup-error'>
					{t('returns.failed', { message: error })}
				</p>
			) : rows === null ? (
				<p className='text-sm text-muted-foreground'>{t('returns.loading')}</p>
			) : (
				<DataTable
					table={table}
					withPagination={false}
					emptyText={<span data-testid='returns-pickup-empty'>{t('returns.pickupEmpty')}</span>}
					row={{
						attributes: (row) => ({
							'data-testid': `returns-pickup-${row.parcelId}`,
							'data-reason': row.reason,
						}),
					}}
				/>
			)}
		</section>
	)
}
